import { enviroment } from "../../enviroment";
import {
  clarityEvent,
  ClarityEvents,
  claritySet,
  isAnalyticsStopped,
} from "../analytics/clarity";

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    clarity?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

export const AnalyticsEvents = {
  PAGE_VIEW: "page_view",
  ADD_TO_CART: "add_to_cart",
  REMOVE_FROM_CART: "remove_from_cart",
  CART_CLEARED: "cart_cleared",
  BEGIN_CHECKOUT: "begin_checkout",
  PURCHASE: "purchase",
} as const;

type AnalyticsEventName =
  (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

export function trackEvent(
  eventName: AnalyticsEventName | string,
  params?: Record<string, unknown>,
): void {
  try {
    // Flags REACT_APP_CLARITY_CONSENT_ENABLED / REACT_APP_CLARITY_ENABLED:
    // after Decline/withdrawal, on the no-analytics routes and for internal
    // users the facade is stopped - gtag.js may still be loaded, but nothing
    // is sent. Never true with the flags off.
    if (isAnalyticsStopped()) return;
    if (typeof window.gtag === "function") {
      window.gtag("event", eventName, params);
    }
  } catch {
    // Silently ignore — analytics must never break the app
  }
}

export function trackPageView(path: string, title?: string): void {
  trackEvent(AnalyticsEvents.PAGE_VIEW, {
    page_path: path,
    page_title: title ?? document.title,
  });
}

export function trackAddToCart(
  itemType: "Logo" | "Design",
  itemId: string,
): void {
  trackEvent(AnalyticsEvents.ADD_TO_CART, {
    item_type: itemType,
    item_id: itemId,
  });
  // Flag REACT_APP_CLARITY_EVENTS_ENABLED: fan-out of the funnel step to
  // Clarity (no payload) - 0 new call sites, no-op with the flag off.
  clarityEvent(ClarityEvents.ADD_TO_ORDER);
}

export function trackRemoveFromCart(itemId: string): void {
  trackEvent(AnalyticsEvents.REMOVE_FROM_CART, {
    item_id: itemId,
  });
}

export function trackCartCleared(): void {
  trackEvent(AnalyticsEvents.CART_CLEARED);
}

export function trackBeginCheckout(itemCount: number): void {
  trackEvent(AnalyticsEvents.BEGIN_CHECKOUT, {
    item_count: itemCount,
  });
  clarityEvent(ClarityEvents.CART_CONTINUE);
}

export function trackPurchase(orderId: number, orgId: number): void {
  trackEvent(AnalyticsEvents.PURCHASE, {
    transaction_id: orderId.toString(),
    organization_id: orgId,
  });
  clarityEvent(ClarityEvents.ORDER_PLACED);
}

// --- Clarity-only events (flag REACT_APP_CLARITY_EVENTS_ENABLED) -----------
// Called on the success paths only (after the API answered), never from
// effects. The GA4 events above are NOT extended. Flag off = no-op.

export function trackLogoUploaded(): void {
  clarityEvent(ClarityEvents.LOGO_UPLOADED);
}

export function trackLogoCreated(): void {
  clarityEvent(ClarityEvents.LOGO_CREATED);
}

export function trackDesignSaved(): void {
  clarityEvent(ClarityEvents.DESIGN_SAVED);
}

export function trackExcelUpload(): void {
  clarityEvent(ClarityEvents.EXCEL_UPLOAD);
}

// An error toast / the route error view was shown. Only the code (HTTP or
// envelope status, "ui" when unknown) travels as the tag fejlkode - never
// the message.
export function trackErrorShown(code?: number | string | null): void {
  // The tag fejlkode belongs to the event: with the events flag off neither
  // is sent (the facade flag alone does not switch the error reporting on).
  if (!enviroment.clarityEventsEnabled) return;
  const normalized =
    code === undefined || code === null || code === "" ? "ui" : String(code);
  claritySet("fejlkode", normalized);
  clarityEvent(ClarityEvents.ERROR_SHOWN);
}
